import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import { connect } from 'react-redux';

import firebase from '../../Firebase/Firebase';
import { ARRIVAL_THRESHOLD_METERS, distanceKm, formatDistance, getCurrentPosition, toCoordinate } from '../../Utils/geo';
import { readPrivateDemandLocation } from '../../Utils/demandLocation';
import { performDemandAction } from '../../Services/demandLifecycle';
import NavigationMenu from './NavigationMenu';
import { useI18n } from '../../i18n/I18nProvider';

const DemandLocationPanel = ({
  demand,
  currentUserId,
  userType,
  allowNavigation = false,
  allowArrivalConfirmation = false,
}) => {
  const { t } = useI18n();
  const [privateLocation, setPrivateLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let active = true;
    const demandId = demand?.demand_id;
    if (!demandId || !allowNavigation) return undefined;

    setLoading(true);
    const db = firebase.firestore();
    readPrivateDemandLocation(db, demandId)
      .then(({ data }) => {
        if (!active) return;
        if (data) setPrivateLocation(data);
      })
      .catch((error) => {
        if (!active) return;
        if (error?.code !== 'permission-denied') setMessage(error.message);
      })
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [allowNavigation, currentUserId, demand?.demand_id]);

  const legacyExactLocation = useMemo(() => {
    if (demand?.public_location) return null;
    const coordinate = toCoordinate(demand?.exact_location || demand?.location);
    if (!coordinate && !demand?.demand_address) return null;
    return {
      ...coordinate,
      exact_address: demand?.demand_address || '',
    };
  }, [demand]);

  const exact = privateLocation || legacyExactLocation;
  const exactCoordinate = toCoordinate(exact);
  const exactAddress = exact?.exact_address || exact?.formattedAddress || demand?.demand_address || '';
  const publicLabel = demand?.public_location?.label
    || (!allowNavigation ? demand?.demand_region : demand?.demand_address)
    || t('location.unspecified');
  const alreadyConfirmed = demand?.arrival_confirmation?.confirmed;

  const confirmArrival = async () => {
    if (!exactCoordinate || !demand?.demand_id || !currentUserId) return;
    setConfirming(true);
    setMessage('');
    try {
      const current = await getCurrentPosition({ maximumAge: 15000 });
      const distance = distanceKm(current, exactCoordinate);
      const distanceMeters = Number.isFinite(distance) ? Math.round(distance * 1000) : null;
      const tolerance = Math.max(ARRIVAL_THRESHOLD_METERS, Math.round(Number(current.accuracy || 0) * 1.5));
      if (!Number.isFinite(distanceMeters) || distanceMeters > tolerance) {
        throw new Error(t('location.farFromJob', { distance: formatDistance(distance) }));
      }
      await performDemandAction({
        demandId: demand.demand_id,
        action: 'confirm_arrival',
        payload: {
          distance_m: distanceMeters,
          accuracy_m: Math.round(Number(current.accuracy || 0)),
        },
      });
      setMessage(t('location.arrivalSuccess'));
    } catch (error) {
      setMessage(error.message || t('location.arrivalError'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Panel>
      <Header>
        <LocationOnOutlinedIcon />
        <div>
          <span>{allowNavigation ? t('location.jobAddress') : t('location.jobArea')}</span>
          <strong>{allowNavigation && exactAddress ? exactAddress : publicLabel}</strong>
        </div>
      </Header>

      {allowNavigation ? (
        <Actions>
          <NavigationMenu
            location={exactCoordinate}
            address={exactAddress}
            disabled={loading || (!exactCoordinate && !exactAddress)}
            compact
          />
          {allowArrivalConfirmation && !alreadyConfirmed && (
            <ArrivalButton type="button" onClick={confirmArrival} disabled={confirming || !exactCoordinate}>
              <MyLocationOutlinedIcon /> {confirming ? t('location.checking') : t('location.arrived')}
            </ArrivalButton>
          )}
          {alreadyConfirmed && <Confirmed><CheckCircleOutlineIcon /> {t('location.arrivalConfirmed')}</Confirmed>}
        </Actions>
      ) : (
        <PrivacyNote>{t('location.privacy')}</PrivacyNote>
      )}
      {message && <Message>{message}</Message>}
    </Panel>
  );
};

const Panel = styled.div`width: 100%; min-width: 0; display: grid; gap: .95rem; margin-top: 1rem; padding: 1.1rem 1.15rem; border: 1px solid #d9e6ed; border-radius: 1.05rem; background: #f4f9fb; overflow: hidden;`;

const Header = styled.div`
  display: flex; align-items: flex-start; gap: .8rem; min-width: 0; color: #111111;
  > svg { flex: 0 0 auto; margin-top: .15rem; }
  div { display: grid; min-width: 0; }
  span { color: #6e7f8b; font-size: .7rem; font-weight: 850; letter-spacing: .05em; text-transform: uppercase; margin-bottom: .18rem; }
  strong { color: #183a50; font-size: 1rem; line-height: 1.5; overflow-wrap: anywhere; }
`;
const Actions = styled.div`min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: .75rem;`;

const ArrivalButton = styled.button`display: inline-flex; align-items: center; gap: .4rem; min-height: 2.55rem; padding: .55rem .75rem; border: 0; border-radius: 999px; background: #111111; color: #fff; font: inherit; font-size: .8rem; font-weight: 850; cursor: pointer; &:disabled { opacity: .5; cursor: not-allowed; } svg { font-size: 1rem; }`;
const Confirmed = styled.span`display: inline-flex; align-items: center; gap: .35rem; color: #216a43; font-size: .78rem; font-weight: 850; svg { font-size: 1rem; }`;
const PrivacyNote = styled.p`margin: 0; color: #667986; font-size: .79rem; line-height: 1.6;`;

const Message = styled.p`margin: 0; color: #5f4c24; font-size: .74rem; line-height: 1.45;`;

const mapStateToProps = ({ firebase: firebaseState }) => ({
  currentUserId: firebaseState?.auth?.uid,
  userType: firebaseState?.profile?.user_type,
});

export default connect(mapStateToProps)(DemandLocationPanel);
